// מסך 12 — אתר דמו לבדיקת באנרים: דף תוכן בעברית עם מיקומי פרסום, באנרים חיים ב-iframe מה-sandbox,
// בורר רוחב viewport (לא משנה את ה-ZIP), טעינה מיידית או lazy, ובדיקה אוטומטית.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, waitForJob, type Job } from '../api';
import Icon from '../components/Icon';
import { Empty, ErrorBox, Field, fmtDate, Loading, useLoad, useToast } from '../components/ui';
import DemoSlot, { type Assignment, type DemoPackage, type Slot } from '../components/demo/DemoSlot';
import { PackagesPanel, TestPanel } from '../components/demo/DemoPanels';
import '../editor.css';

interface PageView { page: { id: string; name: string; campaign_id: string | null; created_at: string }; slots: Slot[]; assignments: Assignment[] }
const VIEWPORTS: { key: string; label: string; width: number }[] = [
  { key: 'desktop', label: 'דסקטופ 1280', width: 1280 },
  { key: 'tablet', label: 'טאבלט 768', width: 768 },
  { key: 'mobile', label: 'מובייל 375', width: 375 },
];

interface TestItem { slotName: string; width: number; height: number; passed: boolean; load: boolean; loadError?: string | null; consoleErrors: string[]; missingAssets: string[]; externalRequests: string[]; click: { match: boolean | null; blocked: boolean; duplicate: boolean; found: string | null; expected: string | null }; finalFrameOk: boolean | null; textOverflow: unknown[] }

function problemsOf(r: TestItem): string[] {
  const out: string[] = [];
  if (!r.load) out.push(r.loadError ? `לא נטען: ${r.loadError}` : 'הבאנר לא נטען');
  if (r.consoleErrors?.length) out.push(`שגיאות בקונסול (${r.consoleErrors.length})`);
  if (r.missingAssets?.length) out.push(`קבצים חסרים (${r.missingAssets.length})`);
  if (r.externalRequests?.length) out.push('פנייה לשרת חיצוני');
  if (r.click?.blocked) out.push('הקליק לא נקלט');
  else if (r.click?.duplicate) out.push('הקליק נפתח פעמיים');
  else if (r.click?.match === false) out.push('הקליק לא מוביל לקישור שנקבע');
  if (r.finalFrameOk === false) out.push('פריים הסיום לא תקין');
  if (r.textOverflow?.length) out.push('טקסט גולש');
  return out;
}

/** בדיקת קמפיין בלחיצה אחת: מכין יצוא + דף דמו עם כל הגדלים ובודק */
function CampaignRunner({ campaigns, onDone }: { campaigns: any[]; onDone: (pageId: string) => void }) {
  const [params] = useSearchParams();
  const [cid, setCid] = useState(params.get('campaign') ?? '');
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<{ pageId: string; variants: number; ready: number; failures: { variant: string; reason: string }[]; test: { results: TestItem[]; passed: boolean } } | null>(null);
  const started = useRef(false);

  const run = useCallback(async (id: string) => {
    if (!id) return;
    setBusy(true); setErr(null); setRes(null); setJob(null);
    try {
      const { jobId } = await api.post<{ jobId: string }>(`/api/campaigns/${id}/test-run`);
      const j = await waitForJob(jobId, setJob, 15 * 60_000);
      if (j.status !== 'succeeded') throw new Error(j.error ?? 'הבדיקה נכשלה');
      setRes(j.result as any);
      onDone((j.result as any).pageId);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }, [onDone]);

  useEffect(() => { if (params.get('run') === '1' && cid && !started.current) { started.current = true; void run(cid); } }, [cid, params, run]);

  const step = (job?.progress as any)?.step;
  const prog = job?.progress as any;
  const results = res?.test.results ?? [];
  const bad = results.filter((r) => !r.passed);
  return (
    <section className="card stack" aria-labelledby="run-h">
      <h2 id="run-h" style={{ margin: 0 }}>בדיקת קמפיין באתר דמו</h2>
      <p className="muted" style={{ margin: 0 }}>בוחרים קמפיין, והמערכת מעלה את כל הבאנרים שלו לאתר חדשות לדוגמה, כל אחד במקום שמתאים לגודל שלו, ובודקת שהכול נטען ושהקליק מוביל לקישור הנכון.</p>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <Field label="קמפיין">
          <select value={cid} onChange={(e) => setCid(e.target.value)} style={{ minWidth: 260 }}>
            <option value="">— בחרו —</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.client_name} · {c.name}</option>)}
          </select>
        </Field>
        <button className="btn primary" disabled={!cid || busy} onClick={() => run(cid)}>{busy ? <><span className="spinner" /> בודק…</> : <><Icon name="play" size={18} />העלה והתחל בדיקה</>}</button>
      </div>
      {busy && <div className="alert info">{step === 'export' ? `מכין חבילה ${(prog.done ?? 0) + 1} מתוך ${prog.total}${prog.name ? ` (${prog.name})` : ''}…` : step === 'test' ? 'בודק בדפדפן את כל הבאנרים באתר…' : 'מתחיל…'}</div>}
      <ErrorBox error={err} />
      {res && (
        <div className="stack" role="status">
          <div className={`alert ${res.test.passed && !res.failures.length ? 'ok' : 'warn'}`}>
            <strong>{res.test.passed && !res.failures.length ? 'כל הבאנרים תקינים' : `נמצאו ${bad.length + res.failures.length} בעיות`}</strong>
            {' '}· {results.length - bad.length} מתוך {res.variants} באנרים עברו את הבדיקה
          </div>
          <table className="table small"><thead><tr><th>מיקום</th><th>גודל</th><th>תוצאה</th></tr></thead>
            <tbody>
              {results.map((r, i) => { const pr = problemsOf(r); return (
                <tr key={i}><td>{r.slotName}</td><td className="ltr">{r.width}×{r.height}</td>
                  <td>{r.passed ? <span className="badge ok"><Icon name="check" size={14} />תקין</span> : <span className="badge err"><Icon name="alert" size={14} />{pr.join(' · ') || 'לא עבר'}</span>}</td></tr>); })}
              {res.failures.map((f, i) => <tr key={`f${i}`}><td>{f.variant}</td><td /><td><span className="badge err"><Icon name="alert" size={14} />לא נבנה: {f.reason}</span></td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function DemoSite() {
  const toast = useToast();
  const pages = useLoad<PageView['page'][]>('/api/demo-pages');
  const camps = useLoad<any[]>('/api/campaigns?limit=200');
  const [pkgs, setPkgs] = useState<DemoPackage[] | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [view, setView] = useState<PageView | null>(null);
  const [vErr, setVErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [cid, setCid] = useState('');
  const [creating, setCreating] = useState(false);
  const [cErr, setCErr] = useState<string | null>(null);
  const [vp, setVp] = useState(VIEWPORTS[0]);
  const [template, setTemplate] = useState<'desktop' | 'mobile'>('desktop');
  const [lazy, setLazy] = useState(false);
  const [replay, setReplay] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const [wrapEl, setWrapEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => setWrapEl(wrap.current), [view]);

  const loadPkgs = useCallback(() => { api.get<DemoPackage[]>('/api/demo-packages').then(setPkgs).catch(() => setPkgs([])); }, []);
  useEffect(loadPkgs, [loadPkgs]);
  useEffect(() => { if (!pageId && pages.data?.length) setPageId(pages.data[0].id); }, [pages.data, pageId]);
  const loadPage = useCallback(async () => {
    if (!pageId) return;
    setVErr(null);
    try { setView(await api.get<PageView>(`/api/demo-pages/${pageId}`)); } catch (e: any) { setVErr(e.message); }
  }, [pageId]);
  useEffect(() => { void loadPage(); }, [loadPage]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true); setCErr(null);
    try {
      const r = await api.post<PageView>('/api/demo-pages', { name: name.trim(), ...(cid ? { campaignId: cid } : {}) });
      setName(''); pages.reload(); setPageId(r.page.id); setView(r);
    } catch (x: any) { setCErr(x.message); } finally { setCreating(false); }
  };
  const assign = async (slotId: string, demoPackageId: string) => {
    const r = await api.post<PageView>(`/api/demo-pages/${pageId}/assignments`, { slotId, demoPackageId });
    setView(r); toast('הבאנר שובץ');
  };
  const unassign = async (assignmentId: string) => { setView(await api.del<PageView>(`/api/demo-pages/${pageId}/assignments/${assignmentId}`)); };
  const setViewport = (v: typeof VIEWPORTS[number]) => { setVp(v); setTemplate(v.width < 600 ? 'mobile' : 'desktop'); };

  const byPos = useMemo(() => {
    const m: Record<string, Slot[]> = {};
    for (const s of view?.slots ?? []) (m[s.position] ??= []).push(s);
    return m;
  }, [view]);
  const asgFor = (slotId: string) => view?.assignments.find((a) => a.slot_id === slotId) ?? null;
  const slotEl = (s: Slot) => <DemoSlot key={s.id} slot={s} assignment={asgFor(s.id)} packages={pkgs ?? []} lazy={lazy} replayKey={replay} scrollRoot={wrapEl} onAssign={assign} onUnassign={unassign} />;
  const campName = (id: string | null) => camps.data?.find((c) => c.id === id)?.name;

  return (
    <div className="stack">
      <div className="page-title"><div><h1>אתר דמו</h1><p>אתר חדשות לדוגמה שבו הבאנרים של הקמפיין מוצגים במקומות המתאימים להם.</p></div></div>
      {camps.data && <CampaignRunner campaigns={camps.data} onDone={(id) => { pages.reload(); setPageId(id); void api.get<PageView>(`/api/demo-pages/${id}`).then(setView); setReplay((x) => x + 1); }} />}
      <div className="dm-layout">
        <details className="stack" style={{ alignSelf: 'start' }}>
          <summary className="btn small">דפי דמו וחבילות ידניים (מתקדם)</summary>
          <section className="card" aria-labelledby="pages-h" style={{ marginTop: 8 }}>
            <h2 id="pages-h" style={{ marginTop: 0 }}>דפי דמו</h2>
            <ErrorBox error={pages.error} />
            {pages.data?.length === 0 && <div className="small muted">אין דפים — צרו דף ראשון.</div>}
            <ul className="iss-list">
              {pages.data?.map((p) => (
                <li key={p.id}><button className="linkish" aria-current={p.id === pageId ? 'page' : undefined} style={{ fontWeight: p.id === pageId ? 700 : 400 }} onClick={() => setPageId(p.id)}>{p.name}</button> <span className="small muted">{campName(p.campaign_id) ?? ''} · {fmtDate(p.created_at)}</span></li>
              ))}
            </ul>
            <form onSubmit={create} className="stack" style={{ marginTop: 8 }}>
              <Field label="שם דף חדש"><input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required /></Field>
              <Field label="קמפיין">
                <select value={cid} onChange={(e) => setCid(e.target.value)}>
                  <option value="">ללא</option>
                  {camps.data?.map((c) => <option key={c.id} value={c.id}>{c.client_name} · {c.name}</option>)}
                </select>
              </Field>
              <ErrorBox error={cErr} />
              <button className="btn primary" disabled={creating || !name.trim()}>יצירת דף</button>
            </form>
          </section>
          <PackagesPanel packages={pkgs} reload={loadPkgs} />
        </details>

        <div className="stack" style={{ minWidth: 0 }}>
          {!pageId ? <Empty>בחרו או צרו דף דמו.</Empty> : vErr ? <ErrorBox error={vErr} /> : !view ? <Loading /> : (
            <>
              <div className="card">
                <div className="row" role="toolbar" aria-label="תצוגת הדמו">
                  <div className="seg" role="group" aria-label="רוחב viewport">
                    {VIEWPORTS.map((v) => <button key={v.key} className={vp.key === v.key ? 'on' : ''} aria-pressed={vp.key === v.key} onClick={() => setViewport(v)}>{v.label}</button>)}
                  </div>
                  <div className="seg" role="group" aria-label="תבנית">
                    <button className={template === 'desktop' ? 'on' : ''} aria-pressed={template === 'desktop'} onClick={() => setTemplate('desktop')}>תבנית דסקטופ</button>
                    <button className={template === 'mobile' ? 'on' : ''} aria-pressed={template === 'mobile'} onClick={() => setTemplate('mobile')}>תבנית מובייל</button>
                  </div>
                  <div className="seg" role="group" aria-label="מצב טעינה">
                    <button className={!lazy ? 'on' : ''} aria-pressed={!lazy} onClick={() => { setLazy(false); setReplay((x) => x + 1); }}>טעינת הכל מיד</button>
                    <button className={lazy ? 'on' : ''} aria-pressed={lazy} onClick={() => { setLazy(true); setReplay((x) => x + 1); }}>lazy — בכניסה למסך</button>
                  </div>
                  <button className="btn small" onClick={() => setReplay((x) => x + 1)}>↻ ניגון חוזר לכולם</button>
                  <button className="btn small" onClick={() => { void loadPage(); setReplay((x) => x + 1); }}>טעינת הדף מחדש</button>
                </div>
                <p className="small muted" style={{ marginBottom: 0 }}>כל באנר הוא iframe חי במידותיו המדויקות מה-origin המבודד. שינוי רוחב התצוגה אינו משנה את קובץ ה-ZIP.</p>
              </div>
              <div ref={wrap} className="dm-viewport-wrap" style={{ maxHeight: '78vh' }}>
                <div className="dm-viewport" style={{ width: vp.width }} dir="rtl" lang="he">
                  <header className="dm-header"><strong>החדשות של הדמו</strong><span className="small">אתר תוכן לדוגמה — לבדיקת באנרים בלבד</span></header>
                  <nav className="dm-nav" aria-label="ניווט אתר הדמו">
                    {(template === 'mobile' ? ['ראשי', 'חדשות', 'נדל"ן'] : ['ראשי', 'חדשות', 'כלכלה', 'נדל"ן', 'צרכנות', 'תרבות', 'ספורט']).map((x) => <span key={x}>{x}</span>)}
                  </nav>
                  {template === 'desktop' ? (
                    <>
                      {(byPos.top ?? []).map(slotEl)}
                      <div className="dm-body">
                        <article className="dm-article">
                          <Article inline={(byPos.inline ?? []).map(slotEl)} />
                        </article>
                        <aside className="dm-side" aria-label="סרגל צד">
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, borderBottom: '2px solid #8b1d1d', paddingBottom: 4 }}>הנקראים ביותר</div>
                          {['שוק הדירות: מה צפוי ברבעון הבא', 'מדריך: איך בוחרים משכנתא', 'עשר שכונות שכדאי להכיר'].map((t) => <div key={t} style={{ fontSize: 14, fontFamily: 'Arial, sans-serif' }}>{t}</div>)}
                          {(byPos.side ?? []).map(slotEl)}
                        </aside>
                      </div>
                    </>
                  ) : (
                    <>
                      {(byPos.mobile_top ?? []).map(slotEl)}
                      <div className="dm-body mobile">
                        <article className="dm-article"><Article inline={(byPos.mobile_inline ?? []).map(slotEl)} /></article>
                      </div>
                    </>
                  )}
                  <footer className="dm-nav" style={{ borderTop: '1px solid #ddd' }}>© אתר דמו · כל הזכויות שמורות לאף אחד</footer>
                </div>
              </div>
              <TestPanel key={view.page.id} pageId={view.page.id} defaultViewport={template} lazy={lazy} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Article({ inline }: { inline: React.ReactNode }) {
  const ps = [
    'בשבועות האחרונים נרשמה עלייה בפעילות בשוק הדיור באזור המרכז, כאשר יזמים מדווחים על ביקוש גובר לדירות גדולות. לדברי אנשי מקצוע, חלק מהקונים מחפשים מרחב נוסף לעבודה מהבית.',
    'הנתונים שפורסמו מצביעים על כך שמספר העסקאות גדל בהשוואה לתקופה המקבילה אשתקד, אך המחירים נותרו יציבים יחסית. כלכלנים מעריכים שהמגמה תימשך גם ברבעון הקרוב.',
    'במקביל, הרשויות המקומיות מקדמות תוכניות להרחבת התחבורה הציבורית, צעד שעשוי להשפיע על האטרקטיביות של שכונות חדשות. תושבים ותיקים מביעים תקווה שהשיפור יקל על העומס בכבישים.',
    'מומחים ממליצים לרוכשים לבדוק היטב את תנאי המימון ולהשוות בין הצעות שונות לפני קבלת החלטה. לדבריהם, תכנון מוקדם יכול לחסוך סכומים משמעותיים לאורך השנים.',
    'בסיכומו של דבר, השוק נמצא בנקודת איזון עדינה. הימים הקרובים יראו אם המגמה הנוכחית תתבסס, או שמא צפויים שינויים בעקבות החלטות הריבית הבאות.',
  ];
  return (
    <>
      <h1>שוק הדיור במרכז: הביקוש לדירות גדולות ממשיך לעלות</h1>
      <p style={{ color: '#666', fontSize: 14, fontFamily: 'Arial, sans-serif' }}>מאת כתב הדמו · עודכן היום</p>
      <p>{ps[0]}</p>
      <p>{ps[1]}</p>
      {inline}
      {ps.slice(2).map((p, i) => <p key={i}>{p}</p>)}
    </>
  );
}
